import type { CoverageEntry } from '../api/masterCertificateApi';

export interface Acord27FormData {
  certDate: Date | null;
  agencyAddress1: string;
  agencyAddress2: string;
  agencyAddress3: string;
  agencyCity: string;
  agencyState: string;
  agencyZip: string;
  agencyPhone: string;
  agencyFax: string;
  agencyEmail: string;
  agencyCode: string;
  agencySubCode: string;
  agencyCustomer: string;
  company: string;
  companyId: string | null;
  companyNaic: string;
  loanNumber: string;
  policyNumber: string;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  continuedUntilTerminated: boolean;
  replacesPriorDate: Date | null;
  insuredAddress1: string;
  insuredAddress2: string;
  insuredAddress3: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
  locationDescription: string;
  coverageRows: { coveragePerilsForms: string; amountOfInsurance: string; deductible: string }[];
  // v2016 perils fields
  perilsInsured?: boolean;
  basic?: boolean;
  broad?: boolean;
  special?: boolean;
  otherCoverageCheck?: boolean;
  otherCoverageText?: string;
  remarks: string;
  // ACORD 101
  showAcord101: boolean;
  acord101Agency: string;
  acord101NamedInsured: string;
  acord101AdditionalRemarks: string;
  // Additional Interest
  additionalInterestName: string;
  aiMortgagee: boolean;
  aiAdditionalInsured: boolean;
  aiLossPayee: boolean;
  aiLenderLossPayable?: boolean;
  aiOtherCheck: boolean;
  aiOtherText: string;
  aiLoanNumber: string;
  authRepSignature: string;
}

function toDisplayDate(d: Date | null): string {
  if (!d) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const y = d.getFullYear();
  return `${m}/${day}/${y}`;
}

function toIsoDateTime(d: Date | null): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}T00:00:00`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildAcord27Xml(d: Acord27FormData): string {
  const rows = Array.from({ length: 10 }, (_, i) => {
    const row = d.coverageRows[i] ?? { coveragePerilsForms: '', amountOfInsurance: '', deductible: '' };
    return `<Other${i + 1} text="${esc(row.coveragePerilsForms)}" value="${esc(row.deductible)}">${esc(row.amountOfInsurance)}</Other${i + 1}>`;
  }).join('');

  return (
    '<Coverages>' +
    '<Acord27AllLiabilities>' +
    `<ProducerCompletionDate>${esc(toDisplayDate(d.certDate))}</ProducerCompletionDate>` +
    `<ProducerFullName>${esc(d.agencyAddress1)}</ProducerFullName>` +
    `<ProducerCityName>${esc(d.agencyCity)}</ProducerCityName>` +
    `<ProducerAddressLineOne>${esc(d.agencyAddress2)}</ProducerAddressLineOne>` +
    `<ProducerAddressLineTwo>${esc(d.agencyAddress3)}</ProducerAddressLineTwo>` +
    `<ProducerPostalCode>${esc(d.agencyZip)}</ProducerPostalCode>` +
    `<ProducerStateOrProvinceCode>${esc(d.agencyState)}</ProducerStateOrProvinceCode>` +
    `<NamedInsuredFullName>${esc(d.insuredAddress1)}</NamedInsuredFullName>` +
    `<NamedInsuredCityName>${esc(d.insuredCity)}</NamedInsuredCityName>` +
    `<NamedInsuredLineOne>${esc(d.insuredAddress2)}</NamedInsuredLineOne>` +
    `<NamedInsuredLineTwo>${esc(d.insuredAddress3)}</NamedInsuredLineTwo>` +
    `<NamedInsuredZIP>${esc(d.insuredZip)}</NamedInsuredZIP>` +
    `<NamedInsuredState>${esc(d.insuredState)}</NamedInsuredState>` +
    `<LoanNumber>${esc(d.loanNumber)}</LoanNumber>` +
    `<ContinuedUntilTerminated>${d.continuedUntilTerminated ? 'Yes' : 'No'}</ContinuedUntilTerminated>` +
    `<PriorEvidanceDate>${esc(toDisplayDate(d.replacesPriorDate))}</PriorEvidanceDate>` +
    `<LocationDescription>${esc(d.locationDescription)}</LocationDescription>` +
    rows +
    `<Remarks>${esc(d.remarks)}</Remarks>` +
    `<Basic>${d.basic ? 'Yes' : 'No'}</Basic>` +
    `<Broad>${d.broad ? 'Yes' : 'No'}</Broad>` +
    `<Special>${d.special ? 'Yes' : 'No'}</Special>` +
    `<OtherCoverage text="${esc(d.otherCoverageText ?? '')}">${d.otherCoverageCheck ? 'Yes' : 'No'}</OtherCoverage>` +
    `<PerilsInsured>${d.perilsInsured ? 'Yes' : 'No'}</PerilsInsured>` +
    `<Phone>${esc(d.agencyPhone)}</Phone>` +
    `<Email>${esc(d.agencyEmail)}</Email>` +
    `<Fax>${esc(d.agencyFax)}</Fax>` +
    `<AgencyCode>${esc(d.agencyCode)}</AgencyCode>` +
    `<AgencySubCode>${esc(d.agencySubCode)}</AgencySubCode>` +
    `<AgencyCustomer>${esc(d.agencyCustomer)}</AgencyCustomer>` +
    `<Company>${esc(d.company)}</Company>` +
    `<AdditionalInterestName>${esc(d.additionalInterestName)}</AdditionalInterestName>` +
    `<Mortgagee>${d.aiMortgagee ? 'Yes' : 'No'}</Mortgagee>` +
    `<AdditionalInsured>${d.aiAdditionalInsured ? 'Yes' : 'No'}</AdditionalInsured>` +
    `<LossPayee>${d.aiLossPayee ? 'Yes' : 'No'}</LossPayee>` +
    `<LenderLossPayable>${d.aiLenderLossPayable ? 'Yes' : 'No'}</LenderLossPayable>` +
    `<OtherInterest text="${d.aiOtherCheck ? 'Yes' : 'No'}">${esc(d.aiOtherText)}</OtherInterest>` +
    `<AdditionalInterestLoanNumber>${esc(d.aiLoanNumber)}</AdditionalInterestLoanNumber>` +
    '</Acord27AllLiabilities>' +
    '</Coverages>'
  );
}


function buildAcord101Xml(d: Acord27FormData): string {
  return (
    `<Coverages><Acord101AdditionalRemarks>` +
    `<RemarksText>${esc(d.acord101AdditionalRemarks)}</RemarksText>` +
    `</Acord101AdditionalRemarks></Coverages><Limits />`
  );
}

function buildAcord27AdditionalInterestXml(d: Acord27FormData): string {
  return (
    '<Coverages><CertificateHolderInformationCoverage>' +
    '<AdditionalInterest>' +
    `<NameAndAddress>${esc(d.additionalInterestName)}</NameAndAddress>` +
    `<AdditionalInsured>${d.aiAdditionalInsured}</AdditionalInsured>` +
    `<LendersLossPayable>${d.aiLenderLossPayable ?? false}</LendersLossPayable>` +
    `<LossPayee>${d.aiLossPayee}</LossPayee>` +
    `<Mortage>${d.aiMortgagee}</Mortage>` +
    `<Other>${d.aiOtherCheck}</Other>` +
    `<OtherDescription>${esc(d.aiOtherText)}</OtherDescription>` +
    `<LoanNumber>${esc(d.aiLoanNumber)}</LoanNumber>` +
    `<LenderServicingAgentNameAndAddress></LenderServicingAgentNameAndAddress>` +
    '</AdditionalInterest>' +
    '</CertificateHolderInformationCoverage></Coverages>'
  );
}

export function buildAcord27Coverages(d: Acord27FormData, acordFormNumber = 27): CoverageEntry[] {
  const sig = d.authRepSignature || null;
  const entries: CoverageEntry[] = [
    {
      PolicyNumber:        d.policyNumber,
      NaicId:              d.companyId || null,
      EffectiveDate:       toIsoDateTime(d.effectiveDate),
      ExpirationDate:      toIsoDateTime(d.expirationDate),
      AcordFormNumber:     acordFormNumber,
      CoveragesAndLimits:  buildAcord27Xml(d),
      SignatureId:         sig,
    },
  ];
  if (d.showAcord101) {
    entries.push({
      PolicyNumber:        '',
      NaicId:              null,
      EffectiveDate:       '',
      ExpirationDate:      '',
      AcordFormNumber:     101,
      CoveragesAndLimits:  buildAcord101Xml(d),
      SignatureId:         sig,
    });
  }
  if (d.additionalInterestName) {
    entries.push({
      PolicyNumber:        d.policyNumber,
      NaicId:              d.companyId || null,
      EffectiveDate:       toIsoDateTime(d.effectiveDate),
      ExpirationDate:      toIsoDateTime(d.expirationDate),
      AcordFormNumber:     2716,
      CoveragesAndLimits:  buildAcord27AdditionalInterestXml(d),
      SignatureId:         sig,
    });
  }
  return entries;
}
